<script setup>
import BnDayOfWeekToggle from '@/components/BnDayOfWeekToggle.vue';
import BnTimePicker from '@/components/BnTimePicker.vue';
import { nextOccurrences } from '@/common/medicationSchedule';
import { filledTimes, grownTimes, INTERVAL_UNIT_OPTIONS, MINIMUM_INTERVAL_UNIT_OPTIONS, normalizeWindow, resizeTimes, usesDayOfWeek, windowFrequency, windowRepeat } from '@/common/medicationWindow';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { format, parseISO } from 'date-fns';
import { computed, ref, watch } from 'vue';

/**
 * The fields one administration window answers, shared by the schedule form and every
 * taper step so the two cannot drift apart again. What bounds the window — a start and an
 * ends[x], or a 'Continue for' — stays with the host, which is the only thing that
 * genuinely differs between them.
 */
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    // Prefixes every data-cy in here, so a host with several windows on screen at once —
    // a taper — addresses each one distinctly.
    dataCy: {
        type: String,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    // Whether the recurrence row is on screen at all. A taper shows it on every step,
    // including the one the order describes — locked there, so the step the taper descends
    // FROM reads in the same words as the steps that descend from it, rather than as a
    // sentence about what is missing. An ordinary schedule segment leaves it off: the order
    // owns the recurrence and the sig above already states it.
    showRecurrence: {
        type: Boolean,
        default: false,
    },
    // The interval belongs to the order on the window the order describes, and to the
    // window itself on any later one — which is what makes a taper able to slow down.
    intervalEditable: {
        type: Boolean,
        default: false,
    },
    // Frequency is stated as a number beside the interval, the way the order states it,
    // rather than only as the count of time slots below. A schedule segment leaves this
    // off: it has to match the order's frequency, so a control here would only be a way to
    // break that match.
    frequencyEditable: {
        type: Boolean,
        default: false,
    },
    orderFrequency: {
        type: Number,
        default: null,
    },
    // The window's own start, so the preview walks the recurrence the pass will run. A
    // taper step takes it from the chain rather than from a field.
    previewFrom: {
        type: String,
        default: null,
    },
    previewEnd: {
        type: String,
        default: null,
    },
    showPreview: {
        type: Boolean,
        default: true,
    },
    // Resolved by the host: a taper renders one of these per step, and a concept query
    // per step would fetch the same list several times over.
    holdReasons: {
        type: Array,
        default: () => [],
    },
    // A preset supplies its times and days when it is applied to a patient, so its steps state
    // neither. Every other host leaves both on.
    showTimes: {
        type: Boolean,
        default: true,
    },
    showDaysOfWeek: {
        type: Boolean,
        default: true,
    },
    // A preset step leaves the window to the account's when it is applied, so it offers no
    // field for it (Jayte, 2026-10-23). Every other host leaves it on.
    showWindow: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['patch', 'update:invalid']);

// A time the picker cannot read leaves the model on its previous value, so an unreadable
// slot has to reach the host or it would save a time the field is not showing.
const invalidTimes = ref(new Set());

// What the window says it gives, not what its slots currently amount to. A window that
// predates the field falls back to counting them.
const frequency = computed(() => (Number(props.modelValue?.frequency) > 0 ? Number(props.modelValue.frequency) : windowFrequency(state.value)));

const frequencyOptions = Array.from({ length: 10 }, (item, index) => String(index + 1));

// Stating a frequency resizes the slots to match: two a day is two time pickers, and three
// a week across three days is one. Adding a time afterwards does not restate it — the host's
// mismatch message is what speaks to a count that no longer matches.
const setFrequency = (value) => {
    const count = Number(value) || 1;
    // Shrinking drops slots off the end, so anything flagged past the new length is gone.
    keepInvalidTimes((index) => index < count);
    emit('patch', (current) => ({ frequency: count, times: resizeTimes(current, count) }));
};

/**
 * Re-key the invalid set after the slots move.
 *
 * The flags are positional, so removing a slot shifts every later one down. Left alone, a flag stranded past the end keeps
 * `update:invalid` true forever and the host's Save never re-enables, with nothing on screen
 * saying why.
 */
const keepInvalidTimes = (keep, shift = () => 0) => {
    const next = new Set();
    for (const index of invalidTimes.value) {
        if (keep(index)) next.add(index + shift(index));
    }
    invalidTimes.value = next;
    emit('update:invalid', next.size > 0);
};

const setTimeInvalid = (index, isInvalid) => {
    const next = new Set(invalidTimes.value);
    if (isInvalid) next.add(index);
    else next.delete(index);
    invalidTimes.value = next;
    emit('update:invalid', next.size > 0);
};

// Offered, not enforced: a combobox, so an uncommon interval is still typeable.
const intervalOptions = Array.from({ length: 30 }, (item, index) => String(index + 1));

// `state` rather than `window`: the browser global of that name is in scope in a template.
const state = computed(() => props.modelValue);

const isWeekly = computed(() => usesDayOfWeek(state.value));

/**
 * Report a change as a function of the window's current value rather than as a finished
 * object. A form that writes two fields inside one tick would otherwise compute the second
 * from the props this component was last rendered with and silently drop the first.
 */
const patch = (changes) => emit('patch', () => changes);

const previewDays = computed(() => {
    if (!props.showPreview || state.value.type !== 'scheduled' || !props.previewFrom) return [];
    const repeat = windowRepeat(state.value);
    if (!repeat.timeOfDay.length) return [];
    if (isWeekly.value && !repeat.dayOfWeek.length) return [];
    const boundsPeriod = { start: props.previewFrom };
    if (props.previewEnd) boundsPeriod.end = props.previewEnd;
    return nextOccurrences({ timing: { repeat: { ...repeat, boundsPeriod } } }, { from: props.previewFrom, limit: 3 }).map((day) => format(parseISO(day), 'EEE MMM d'));
});

// Choosing days, or widening the interval past a day, changes how many times the window
// needs. Grow to suit through the same path the day toggle uses rather than a watcher —
// this component is rendered once per taper step, and a watcher per step would race.
const patchRecurrence = (changes) =>
    emit('patch', (current) => {
        // Normalised before the times are grown: dropping the weekdays is what decides how
        // many time slots a now-daily window needs.
        const next = normalizeWindow({ ...current, ...changes });
        // A window that states its own frequency grows to that; one that takes it from the
        // order grows to the order's.
        return { ...changes, dayOfWeek: next.dayOfWeek, times: grownTimes(next, props.frequencyEditable ? frequency.value : props.orderFrequency) };
    });

const setTime = (index, value) => emit('patch', (current) => ({ times: current.times.map((time, position) => (position === index ? value : time)) }));

// A slot removed from the middle has to take its picker with it. Keyed by index, the picker
// holding an unreadable entry stays on screen for the slot after it, and the flag moves to a
// picker that reads fine - Save stays closed with nothing on screen saying why. Slots are only
// ever added or trimmed at the end, except by removeTime, which splices its own key.
let lastSlotKey = 0;
const slotKeys = ref([]);

watch(
    () => (state.value?.times || []).length,
    (length) => {
        const keys = slotKeys.value.slice(0, length);
        while (keys.length < length) keys.push(++lastSlotKey);
        slotKeys.value = keys;
    },
    { immediate: true },
);

const addTime = () => emit('patch', (current) => ({ times: [...current.times, null] }));

const removeTime = (index) => {
    slotKeys.value = slotKeys.value.filter((key, position) => position !== index);
    keepInvalidTimes(
        (position) => position !== index,
        (position) => (position > index ? -1 : 0),
    );
    emit('patch', (current) => ({ times: current.times.filter((time, position) => position !== index) }));
};
</script>

<template>
    <div>
        <template v-if="state.type === 'scheduled'">
            <div class="d-flex flex-wrap align-center">
                <template v-if="showRecurrence">
                    <span class="text-caption text-medium-emphasis mr-2 mb-5">Repeats</span>
                    <v-combobox
                        :model-value="String(frequency)"
                        :items="frequencyOptions"
                        :disabled="readOnly || !frequencyEditable"
                        label="Frequency"
                        suffix="times"
                        class="mr-2 flex-grow-0 flex-shrink-0"
                        style="width: 130px"
                        :data-cy="`${dataCy}Frequency`"
                        @update:model-value="setFrequency"
                    ></v-combobox>
                    <span class="text-caption text-medium-emphasis mr-2 mb-5">every</span>
                    <v-combobox
                        :model-value="String(state.period)"
                        :items="intervalOptions"
                        :disabled="readOnly || !intervalEditable"
                        label="Interval"
                        class="mr-2 flex-grow-0 flex-shrink-0"
                        style="width: 110px"
                        :data-cy="`${dataCy}Interval`"
                        @update:model-value="(value) => patchRecurrence({ period: Number(value) || 1 })"
                    ></v-combobox>
                    <v-select
                        :model-value="state.periodUnit"
                        :items="INTERVAL_UNIT_OPTIONS"
                        :disabled="readOnly || !intervalEditable"
                        label="Unit"
                        class="flex-grow-0 flex-shrink-0"
                        style="width: 140px"
                        :data-cy="`${dataCy}IntervalUnit`"
                        @update:model-value="(value) => patchRecurrence({ periodUnit: value })"
                    ></v-select>
                </template>
            </div>

            <div v-if="showTimes" :data-cy="`${dataCy}Times`">
                <div class="text-caption text-medium-emphasis">Administration times</div>
                <div class="d-flex flex-wrap align-center">
                    <div v-for="(time, index) in state.times" :key="slotKeys[index] ?? `slot${index}`" class="d-flex align-center mr-1">
                        <!-- The field carries a clock button inside it, so '10:30 PM' has ~36px
                             less room than the box suggests. -->
                        <div class="flex-shrink-0" style="width: 145px">
                            <!-- Administration times land on the clock, not between it. Five
                                 minutes is fine enough for a MAR and keeps the column scannable;
                                 a time already off the step is still offered. -->
                            <BnTimePicker
                                :model-value="time"
                                :disabled="readOnly"
                                :label="`Time ${index + 1}`"
                                :minute-step="5"
                                :data-cy="`${dataCy}Time${index}`"
                                @update:model-value="(value) => setTime(index, value)"
                                @update:invalid="(value) => setTimeInvalid(index, value)"
                            ></BnTimePicker>
                        </div>
                        <v-btn
                            v-if="!readOnly && state.times.length > 1"
                            variant="text"
                            size="small"
                            color="secondary"
                            class="mb-5 px-0"
                            style="min-width: 24px"
                            :aria-label="`Remove time ${index + 1}`"
                            :data-cy="`${dataCy}RemoveTime${index}`"
                            @click="removeTime(index)"
                        >
                            <FontAwesomeIcon :icon="['far', 'trash']" />
                        </v-btn>
                    </div>
                    <v-btn v-if="!readOnly" variant="text" color="primary" class="mb-5" :data-cy="`${dataCy}AddTimeBtn`" @click="addTime">
                        <FontAwesomeIcon :icon="['far', 'plus']" class="mr-1" />
                        Add time
                    </v-btn>
                </div>
            </div>

            <slot name="bounds"></slot>

            <div v-if="isWeekly && showDaysOfWeek" class="mb-2" :data-cy="`${dataCy}DayOfWeek`">
                <div class="text-caption text-medium-emphasis">Days of week</div>
                <BnDayOfWeekToggle :model-value="state.dayOfWeek" :disabled="readOnly" @update:model-value="(value) => patchRecurrence({ dayOfWeek: value })"></BnDayOfWeekToggle>
            </div>

            <div v-if="previewDays.length" class="mb-2 text-caption text-medium-emphasis" :data-cy="`${dataCy}Preview`">Next doses: {{ previewDays.join(' · ') }}</div>

            <slot name="scheduled-messages"></slot>
        </template>

        <template v-if="state.type === 'prn'">
            <div class="d-flex flex-wrap mt-2">
                <v-text-field
                    :model-value="state.maxPerDay"
                    :disabled="readOnly"
                    type="number"
                    min="1"
                    label="Maximum times per day"
                    class="mr-2 flex-grow-1"
                    style="min-width: 150px"
                    :data-cy="`${dataCy}MaxPerDay`"
                    @update:model-value="(value) => patch({ maxPerDay: value })"
                ></v-text-field>
                <v-text-field
                    :model-value="state.minimumIntervalValue"
                    :disabled="readOnly"
                    type="number"
                    min="1"
                    label="Minimum interval"
                    class="mr-2 flex-grow-0"
                    style="width: 150px"
                    :data-cy="`${dataCy}MinimumInterval`"
                    @update:model-value="(value) => patch({ minimumIntervalValue: value })"
                ></v-text-field>
                <v-select
                    :model-value="state.minimumIntervalUnit"
                    :items="MINIMUM_INTERVAL_UNIT_OPTIONS"
                    :disabled="readOnly"
                    label="Unit"
                    class="flex-grow-0"
                    style="width: 130px"
                    :data-cy="`${dataCy}MinimumIntervalUnit`"
                    @update:model-value="(value) => patch({ minimumIntervalUnit: value })"
                ></v-select>
            </div>
            <v-text-field
                :model-value="state.prnIndication"
                :disabled="readOnly"
                label="Indication (optional)"
                :data-cy="`${dataCy}Indication`"
                @update:model-value="(value) => patch({ prnIndication: value })"
            ></v-text-field>

            <slot name="prn-messages"></slot>

            <slot name="bounds"></slot>
        </template>

        <template v-if="state.type === 'hold'">
            <!-- A combobox, not a select: the reason list is a starting point, and a hold whose
                 reason is not on it still has to be recordable. -->
            <v-combobox
                :model-value="state.holdReasonCode"
                :items="holdReasons"
                :return-object="false"
                :disabled="readOnly"
                label="Hold reason"
                class="mt-2"
                :data-cy="`${dataCy}HoldReason`"
                @update:model-value="(value) => patch({ holdReasonCode: value })"
            ></v-combobox>
            <v-textarea
                :model-value="state.holdNotes"
                :disabled="readOnly"
                label="Notes"
                rows="2"
                auto-grow
                :data-cy="`${dataCy}HoldNotes`"
                @update:model-value="(value) => patch({ holdNotes: value })"
            ></v-textarea>

            <slot name="bounds"></slot>
        </template>

        <div v-if="state.type === 'scheduled'" class="d-flex flex-wrap align-center">
            <v-text-field
                v-if="showWindow"
                :model-value="state.administrationWindow"
                :disabled="readOnly"
                type="number"
                min="0"
                label="Administration window (minutes)"
                class="mr-4 flex-grow-1"
                style="min-width: 200px"
                :data-cy="`${dataCy}Window`"
                @update:model-value="(value) => patch({ administrationWindow: value })"
            ></v-text-field>
            <!-- Each window carries its own answer, which is what lets a stretch of home leave be
                 self-administered while the windows either side of it are not. -->
            <v-checkbox
                :model-value="state.selfAdministered === true"
                :disabled="readOnly"
                label="Self-doses"
                color="primary"
                density="compact"
                hide-details
                class="flex-grow-0 mb-5"
                :data-cy="`${dataCy}SelfAdministered`"
                @update:model-value="(value) => patch({ selfAdministered: value === true })"
            ></v-checkbox>
        </div>
    </div>
</template>
